import React, { useCallback, useState, useEffect, useRef } from "react";
import axios from 'axios';
import OptionsDriver from "./OptionsDriver";
import DriverAgencyEntry from './agency/DriverAgencyEntry';
import MyAccountDriver from "./MyAccountDriver/MyAccountDriver";
import MapSelectionDriver from "./FinalLocation/MapSelectionDriver";
import OrderList from "./orderslist/OrderList";
import DeliveryList from "./deliverieslist/DeliveryList";
import OrderDriver from "./orderdriver/OrderDriver";
import DeliveryDriver from "./DeliveryDriver";
import ControlTower from "./controltower/ControlTower";
import DriverResumen from "./Resumen/DriverResumen";
import DriverPackageScanner from "./Scanner/DriverPackageScanner";
import ChooseDeliveries from "./ChooseDeliveries/ChooseDeliveries";
import { DriverExpressDashboard } from './express/DriverExpressDashboard';
import DriverCommunicationView from "../shared/communication/DriverCommunicationView";
import { fetchDriverUnreadCount } from "../shared/communication/api";
import { socket, socketAuthPayload } from "../admin/chat/socket";

const DRIVER_UNREAD_RECONCILIATION_MS = 2 * 60 * 1000;

function readDriverChatDeepLink() {
    if (typeof window === 'undefined') {
        return { shouldOpenChat: false, conversationId: null };
    }

    const params = new URLSearchParams(window.location.search);
    const hash = window.location.hash || '';
    const shouldOpenChat = params.get('driver_chat') === '1' || hash.includes('/driver-chat');
    const rawConversationId = params.get('conversation_id');
    const conversationId = rawConversationId && !Number.isNaN(Number(rawConversationId))
        ? Number(rawConversationId)
        : null;

    return { shouldOpenChat, conversationId };
}

function MainDriver({ user_id, username, onLogout, email, phone_number, onUpdateUser }) {
    const [activeComponent, setActiveComponent] = useState('OptionsDriver');
    const [mainDeliveryPoints, setMainDeliveryPoints] = useState([]);
    const [mainOrder, setMainOrder] = useState(null);
    const [driverLocation, setDriverLocation] = useState(null);
    const [chatUnreadCount, setChatUnreadCount] = useState(0);
    const [initialChatConversationId, setInitialChatConversationId] = useState(null);
    const activeComponentMountedRef = useRef(false);

    const refreshChatUnreadCount = useCallback(async (ensureAssigned = false) => {
        if (!user_id) return;
        try {
            const data = await fetchDriverUnreadCount(user_id, {
                include_support: 0,
                ensure_assigned: ensureAssigned ? 1 : 0,
            });
            setChatUnreadCount(Number(data?.unread_count || 0));
        } catch (error) {
            console.error('driver unread count failed', error);
        }
    }, [user_id]);

    useEffect(() => {
        const applyDeepLink = () => {
            const { shouldOpenChat, conversationId } = readDriverChatDeepLink();
            if (!shouldOpenChat) return;
            setActiveComponent('Chat');
            if (conversationId) {
                setInitialChatConversationId(conversationId);
            }
        };

        applyDeepLink();
        window.addEventListener('hashchange', applyDeepLink);
        window.addEventListener('popstate', applyDeepLink);
        return () => {
            window.removeEventListener('hashchange', applyDeepLink);
            window.removeEventListener('popstate', applyDeepLink);
        };
    }, []);

    useEffect(() => {
        if (!user_id) return undefined;
        let refreshTimer = null;
        const roomPayload = socketAuthPayload({ scope: 'driver', user_id });
        const joinRoom = () => socket.emit('join_communications_scope', roomPayload);
        const scheduleRefresh = () => {
            if (refreshTimer) window.clearTimeout(refreshTimer);
            refreshTimer = window.setTimeout(() => refreshChatUnreadCount(false), 400);
        };
        const refreshFromEvent = (payload) => {
            if (payload?.conversation?.type === 'support_general') return;
            scheduleRefresh();
        };
        const rejoinAndRefresh = () => {
            joinRoom();
            refreshChatUnreadCount(false);
        };
        const handleVisibility = () => {
            if (document.visibilityState === 'visible') refreshChatUnreadCount(false);
        };
        const reconcile = () => {
            if (document.visibilityState === 'visible') refreshChatUnreadCount(false);
        };

        refreshChatUnreadCount(true);
        joinRoom();
        const intervalId = window.setInterval(reconcile, DRIVER_UNREAD_RECONCILIATION_MS);
        document.addEventListener('visibilitychange', handleVisibility);
        socket.on('connect', rejoinAndRefresh);
        socket.on('conversation_message_created', refreshFromEvent);
        socket.on('conversation_updated', refreshFromEvent);
        socket.on('conversation_read_updated', refreshFromEvent);
        return () => {
            if (refreshTimer) window.clearTimeout(refreshTimer);
            window.clearInterval(intervalId);
            document.removeEventListener('visibilitychange', handleVisibility);
            socket.off('connect', rejoinAndRefresh);
            socket.off('conversation_message_created', refreshFromEvent);
            socket.off('conversation_updated', refreshFromEvent);
            socket.off('conversation_read_updated', refreshFromEvent);
            socket.emit('leave_communications_scope', roomPayload);
        };
    }, [refreshChatUnreadCount, user_id]);

    useEffect(() => {
        if (!activeComponentMountedRef.current) {
            activeComponentMountedRef.current = true;
            return undefined;
        }
        if (activeComponent === 'Chat') {
            const timeoutId = window.setTimeout(() => refreshChatUnreadCount(false), 1200);
            return () => window.clearTimeout(timeoutId);
        }
        refreshChatUnreadCount(false);
        return undefined;
    }, [activeComponent, refreshChatUnreadCount]);
    
    
    useEffect(() => {
        if (!user_id) return;

        const API = process.env.REACT_APP_API_URL;
        const token = localStorage.getItem("token");

        // Tuning knobs
        const MIN_INTERVAL_MS = 5000;    // don't POST more often than this
        const MIN_MOVE_M      = 20;      // only POST if moved at least this much

        let watchId;
        let lastSentAt = 0;
        let lastLat = null, lastLng = null;
        let inFlight = false;
        let pending = null;              // latest position received while a POST is in flight
        let stopped = false;

        // Haversine distance in meters
        const distM = (lat1, lon1, lat2, lon2) => {
            if (lat1 == null || lon1 == null || lat2 == null || lon2 == null) return Infinity;
            const R = 6371000;
            const toRad = d => (d * Math.PI) / 180;
            const dLat = toRad(lat2 - lat1);
            const dLon = toRad(lon2 - lon1);
            const a =
            Math.sin(dLat / 2) ** 2 +
            Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
            return 2 * R * Math.asin(Math.sqrt(a));
        };

        const postLocation = async (lat, lng) => {
            inFlight = true;

            // Add a per-request timeout
            const ac = new AbortController();
            const kill = setTimeout(() => ac.abort(), 8000);

            try {
            await fetch(`${API}/api/driver/location`, {
                method: 'POST',
                headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
                },
                body: JSON.stringify({ driver_id: user_id, latitude: lat, longitude: lng }),
                signal: ac.signal,
                keepalive: false, // avoid background beacons here
            });

            lastSentAt = Date.now();
            lastLat = lat; lastLng = lng;
            } catch (e) {
            if (e.name !== 'AbortError') {
                console.error('location POST failed', e);
            }
            } finally {
            clearTimeout(kill);
            inFlight = false;

            // If a newer position arrived while sending, send that next
            if (pending && !stopped) {
                const { lat: pLat, lng: pLng } = pending;
                pending = null;
                maybeSend(pLat, pLng);
            }
            }
        };

        const maybeSend = (lat, lng) => {
            const now = Date.now();
            const intervalOk = now - lastSentAt >= MIN_INTERVAL_MS;
            const movedOk = lastLat == null || distM(lastLat, lastLng, lat, lng) >= MIN_MOVE_M;

            if (!intervalOk || !movedOk) return;

            if (inFlight) {
            // store latest to send right after current POST completes
            pending = { lat, lng };
            } else {
            postLocation(lat, lng);
            }
        };

        if ('geolocation' in navigator) {
            watchId = navigator.geolocation.watchPosition(
            (pos) => {
                const { latitude, longitude } = pos.coords;
                setDriverLocation({ latitude, longitude });
                maybeSend(latitude, longitude);
            },
            (err) => {
                console.error('geolocation error', err);
            },
            {
                enableHighAccuracy: false,  // flip to true if you need finer updates (more battery)
                maximumAge: 10000,          // accept cached position up to 10s old
                timeout: 10000,
            }
            );
        } else {
            // Fallback: very light polling (10s) if watchPosition is unavailable
            const poll = () =>
            navigator.geolocation.getCurrentPosition(
                ({ coords }) => {
                    setDriverLocation({ latitude: coords.latitude, longitude: coords.longitude });
                    maybeSend(coords.latitude, coords.longitude);
                },
                (err) => console.error('geolocation error', err),
                { maximumAge: 10000, timeout: 10000 }
            );
            const pollId = setInterval(poll, 10000);
            // tie cleanup to this fallback
            watchId = { _pollId: pollId };
        }

        // Optional: pause bursts when the tab is hidden
        const onVis = () => { if (document.hidden) lastSentAt = Date.now(); };
        document.addEventListener('visibilitychange', onVis);

        return () => {
            stopped = true;
            document.removeEventListener('visibilitychange', onVis);
            if (watchId && typeof watchId === 'number') {
            navigator.geolocation.clearWatch(watchId);
            } else if (watchId && watchId._pollId) {
            clearInterval(watchId._pollId);
            }
        };
        }, [user_id]);

    // useEffect(() => {
    //     let intervalId;
    //     const token = localStorage.getItem("token");
      
    //     // Function to generate a random point within a given radius (in meters)
    //     const getRandomCoordinates = (latitude, longitude, radius) => {
    //       const earthRadius = 6371e3; // Earth's radius in meters
      
    //       // Convert latitude and longitude from degrees to radians
    //       const lat = latitude * Math.PI / 180;
    //       const lon = longitude * Math.PI / 180;
      
    //       // Random distance and bearing
    //       const dist = Math.sqrt(Math.random()) * radius;
    //       const bearing = Math.random() * 2 * Math.PI;
      
    //       // Convert distance and bearing to latitude and longitude
    //       const newLat = Math.asin(Math.sin(lat) * Math.cos(dist / earthRadius) +
    //                     Math.cos(lat) * Math.sin(dist / earthRadius) * Math.cos(bearing));
    //       const newLon = lon + Math.atan2(Math.sin(bearing) * Math.sin(dist / earthRadius) * Math.cos(lat),
    //                                      Math.cos(dist / earthRadius) - Math.sin(lat) * Math.sin(newLat));
      
    //       // Convert new coordinates from radians to degrees
    //       return {
    //         latitude: newLat * 180 / Math.PI,
    //         longitude: newLon * 180 / Math.PI
    //       };
    //     };
      
    //     intervalId = setInterval(() => {
    //       const { latitude, longitude } = getRandomCoordinates(-12.046374, -77.042793, 5000); // 5 km radius
      
    //       fetch(`${process.env.REACT_APP_API_URL}/api/driver/location`, {
    //         method: 'POST',
    //         headers: {
    //           'Content-Type': 'application/json',
    //           'Authorization': `Bearer ${token}`,
    //         },
    //         body: JSON.stringify({
    //           driver_id: user_id,
    //           latitude,
    //           longitude,
    //         }),
    //       });
    //     }, 5000);
    
    //     // Cleanup function
    //     return () => {
    //         if (intervalId) {
    //             clearInterval(intervalId);
    //         }
    //     };
    // }, [user_id]);

    const handleEntregasClick = () => {
        console.log('entrega');
        setActiveComponent('DeliveryList');
    };
    
    const handleRecojosClick = () => {
        console.log('recojo');
        setActiveComponent('OrderList');
    };


    const handleBackClick = () => {
        setActiveComponent('Map');
         
    };
    
    const handleMenuClick = () => {
        setActiveComponent('OptionsDriver');
    };

    const handleOptionClick = (option) => {
        if (option === 'back') {
            setActiveComponent(activeComponent === 'MapDriver' ? 'driver' : 'MapDriver');
        } else if (option === 'MapSelectionDriver') {
            setActiveComponent('MapSelectionDriver');
        } else if (option === 'OrderList') {
            setActiveComponent('OrderList')
        } else if (option === 'DeliveryList') {
            setActiveComponent('DeliveryList')
        } else if (option === 'OptionsDriver') {
            setActiveComponent('OptionsDriver')
        } else if (option === 'driver') {
            setActiveComponent('driver')
        } else if (option === 'WorkDriver') {
            setActiveComponent('WorkDriver')
        }

        
        
        else {
            setActiveComponent('MyAccountDriver')
            // Handle other options
            
        }
    };
    
    return (
        <div className="container-fluid p-0">
            {activeComponent === 'OptionsDriver' && <OptionsDriver username={username} onOptionClick={handleOptionClick} onBackClick={handleBackClick} onLogout={onLogout} setActiveComponent={setActiveComponent} chatUnreadCount={chatUnreadCount} /> }
            {activeComponent === 'MyAccountDriver' && <MyAccountDriver userId={user_id} onLogout={onLogout} setActiveComponent={setActiveComponent}/> }
            {activeComponent === 'MapSelectionDriver' && <MapSelectionDriver userId={user_id} setActiveComponent={setActiveComponent} /> }
            {activeComponent === 'MisRecojos' && <OrderList setActiveComponent={setActiveComponent} userId={user_id}/> }
            {activeComponent === 'MisEntregas' && <DeliveryList setActiveComponent={setActiveComponent} onOptionClick={handleOptionClick} onBackClick={handleBackClick} userId={user_id}/> }
            {activeComponent === 'Resumen' && <DriverResumen setActiveComponent={setActiveComponent} user_id={user_id}/> }
            {activeComponent === 'ControlTower' && <ControlTower setActiveComponent={setActiveComponent} userId={user_id} driverLocation={driverLocation}/> }
            {activeComponent === 'Chat' && <DriverCommunicationView userId={user_id} setActiveComponent={setActiveComponent} initialConversationId={initialChatConversationId}/> }
            {activeComponent === 'DriverPackageScanner' && <DriverPackageScanner setActiveComponent={setActiveComponent} userId={user_id}/> }
            {activeComponent === 'ChooseDeliveries' && <ChooseDeliveries setActiveComponent={setActiveComponent} userId={user_id}/> }
            {activeComponent === 'DriverExpressDashboard' && <DriverExpressDashboard user_id={user_id} setActiveComponent={setActiveComponent} /> }
            {activeComponent === 'AgencyWorkspace' && <DriverAgencyEntry onBack={() => setActiveComponent('OptionsDriver')} />}
        </div>
    );
}

export default MainDriver;
